import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Globe } from "lucide-react";
import { NAV } from "@/data/content";
import { useBusiness, usePublicSettings } from "@/contexts/SettingsContext";
import { usePublicServices } from "@/contexts/ServicesContext";

export default function Footer() {
  const BUSINESS = useBusiness();
  const settings = usePublicSettings();
  const { services } = usePublicServices();
  // Service links come from each service card's Learn More link (CMS-managed and
  // already limited to approved, live pages by the server). Pages already listed in
  // Quick Links are skipped so the footer never links the same page twice.
  const navUrls = new Set(NAV.map((n) => n.to));
  const serviceLinks = [];
  (services || []).forEach((sv) => {
    const url = sv.learn_more_url;
    if (url && url.startsWith("/") && !navUrls.has(url) && !serviceLinks.some((l) => l.url === url)) {
      serviceLinks.push({ url, label: sv.name });
    }
  });
  return (
    <footer
      className="bg-[#0F172A] text-slate-200 mt-16"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className={`grid grid-cols-1 md:grid-cols-2 ${serviceLinks.length ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-10`}>
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img
                src="/logo-icon.webp"
                alt=""
                width="40"
                height="40"
                loading="lazy"
                data-testid="footer-logo-image"
                className="w-10 h-10 shrink-0 object-contain"
              />
              <span className="font-display font-bold text-lg text-white">
                {BUSINESS.name}
              </span>
            </div>
            {settings.license_number && (
              <p
                data-testid="footer-license"
                className="text-xs text-slate-400 -mt-3 mb-3 tracking-wide"
              >
                {settings.license_number}
              </p>
            )}
            <p className="text-sm text-slate-400 leading-relaxed">
              Residential and commercial cleaning across {BUSINESS.area}.
              Per-job estimates, friendly service, reliable results.
            </p>
          </div>

          {/* Quick links */}
          <div>
            <h2 className="font-display font-semibold text-white mb-4 text-sm tracking-wide uppercase">
              Quick Links
            </h2>
            <ul className="space-y-2 text-sm">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    data-testid={`footer-link-${item.to.replace("/", "") || "home"}`}
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services (from CMS service cards) */}
          {serviceLinks.length > 0 && (
            <div data-testid="footer-services">
              <h2 className="font-display font-semibold text-white mb-4 text-sm tracking-wide uppercase">
                Services
              </h2>
              <ul className="space-y-2 text-sm">
                {serviceLinks.map((l) => (
                  <li key={l.url}>
                    <Link
                      to={l.url}
                      data-testid={`footer-service-${l.url.replace("/", "")}`}
                      className="text-slate-400 hover:text-white transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Contact */}
          <div>
            <h2 className="font-display font-semibold text-white mb-4 text-sm tracking-wide uppercase">
              Contact
            </h2>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2 text-slate-400">
                <Phone className="w-4 h-4 mt-0.5 text-[#34D399]" />
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="footer-phone"
                  className="hover:text-white"
                >
                  {BUSINESS.phone}
                </a>
              </li>
              <li className="flex items-start gap-2 text-slate-400">
                <Mail className="w-4 h-4 mt-0.5 text-[#34D399]" />
                <a
                  href={`mailto:${BUSINESS.email}`}
                  data-testid="footer-email"
                  className="hover:text-white break-all"
                >
                  {BUSINESS.email}
                </a>
              </li>
              <li className="flex items-start gap-2 text-slate-400">
                <MapPin className="w-4 h-4 mt-0.5 text-[#34D399]" />
                <span>{BUSINESS.area}</span>
              </li>
              <li className="flex items-start gap-2 text-slate-400">
                <Globe className="w-4 h-4 mt-0.5 text-[#34D399]" />
                <span>{BUSINESS.website}</span>
              </li>
            </ul>
          </div>

          {/* Social + CTA */}
          <div>
            <h2 className="font-display font-semibold text-white mb-4 text-sm tracking-wide uppercase">
              Follow Us
            </h2>
            <div className="flex gap-3 mb-6">
              <a
                href={BUSINESS.instagramUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                aria-label={`${BUSINESS.name} on Instagram (opens in a new tab)`}
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
              >
                <Instagram className="w-5 h-5" aria-hidden="true" />
              </a>
              <a
                href={BUSINESS.facebookUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-facebook"
                aria-label={`${BUSINESS.name} on Facebook (opens in a new tab)`}
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
              >
                <Facebook className="w-5 h-5" aria-hidden="true" />
              </a>
            </div>
            <Link
              to="/request-estimate"
              data-testid="footer-cta-estimate"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-[#34D399] text-[#0F172A] hover:bg-[#10B981] transition-colors"
            >
              Request an Estimate
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <p data-testid="footer-copyright">
            {settings.footer_text
              ? settings.footer_text.replace(/\{year\}/g, new Date().getFullYear())
              : `© ${new Date().getFullYear()} ${BUSINESS.name}. All rights reserved.`}
          </p>
          <p className="text-slate-400">
            Pricing shown is per-job, not hourly. Final pricing confirmed after review.
          </p>
        </div>
      </div>
    </footer>
  );
}
